import { useEffect, useMemo, useState } from "react";
import {
  Brand,
  Button,
  Flex,
  Icon,
  Icons,
  Menu,
  Paragraph,
  Spin,
} from "@ir/ui";
import { useNavigate, useParams } from "react-router";
import type { OrganizationsBrandsLookupItem } from "@src/domain/dto/organizationsBrands";
import { useGlobalDataLookup } from "@src/presentation/providers/GlobalDataLookupProvider";
import { buildOrgBrandMenuItems } from "@src/presentation/pages/UsersAccess/utils/buildOrgBrandMenuItems";
import {
  findOrgBrandByBrandKey,
  findOrgBrandByKeys,
} from "./findOrgBrandByKeys";
import { isConfigSection } from "../configNav.config";

function filterActiveOrganizations(
  organizations: OrganizationsBrandsLookupItem[],
): OrganizationsBrandsLookupItem[] {
  return organizations
    .filter((organization) => organization.organizationIsActive)
    .map((organization) => ({
      ...organization,
      brands: (organization.brands ?? []).filter((brand) => brand.brandIsActive),
    }))
    .filter((organization) => (organization.brands?.length ?? 0) > 0)
    .sort((a, b) =>
      (a.organizationDisplayName ?? a.organizationName).localeCompare(
        b.organizationDisplayName ?? b.organizationName,
      ),
    );
}

export const ConfigOrgBrandSelector = () => {
  const navigate = useNavigate();
  const { organizationKey, brandKey, section } = useParams<{
    organizationKey?: string;
    brandKey?: string;
    section?: string;
  }>();
  const { organizationsBrandsLookup, organizationsBrandsLookupLoading } =
    useGlobalDataLookup();
  const [selectedBrandKey, setSelectedBrandKey] = useState<string | null>(
    brandKey ?? null,
  );

  const selectableOrganizations = useMemo(
    () => filterActiveOrganizations(organizationsBrandsLookup ?? []),
    [organizationsBrandsLookup],
  );

  const menuItems = useMemo(
    () => buildOrgBrandMenuItems(selectableOrganizations),
    [selectableOrganizations],
  );

  useEffect(() => {
    setSelectedBrandKey(brandKey ?? null);
  }, [brandKey]);

  const selectedMatch = useMemo(() => {
    if (!selectedBrandKey) {
      return null;
    }
    if (organizationKey) {
      const byKeys = findOrgBrandByKeys(
        selectableOrganizations,
        organizationKey,
        selectedBrandKey,
      );
      if (byKeys) {
        return byKeys;
      }
    }
    return findOrgBrandByBrandKey(selectableOrganizations, selectedBrandKey);
  }, [selectableOrganizations, organizationKey, selectedBrandKey]);

  const handleMenuSelect = (key: string) => {
    const match = findOrgBrandByBrandKey(selectableOrganizations, key);
    if (!match) {
      return;
    }

    setSelectedBrandKey(key);
    const sectionSegment =
      isConfigSection(section) && organizationKey && brandKey
        ? `/${section}`
        : "";
    navigate(
      `/configuration/${match.organization.organizationKey}/${match.brand.brandKey}${sectionSegment}`,
    );
  };

  const selectedBrandLabel =
    selectedMatch?.brand.brandDisplayName ?? selectedMatch?.brand.brandName;
  const selectedOrgLabel =
    selectedMatch?.organization.organizationDisplayName ??
    selectedMatch?.organization.organizationName;

  let menuContent = (
    <Flex align="center" gap={0.5} style={{ minWidth: 0 }}>
      <Icon name={Icons.Organization} />
      <Paragraph className="m-0 text-grey-500 font-bold text-truncate">
        Select organization and brand
      </Paragraph>
    </Flex>
  );

  if (organizationsBrandsLookupLoading) {
    menuContent = <Spin size="small" />;
  } else if (selectedMatch) {
    menuContent = (
      <Flex align="center" gap={0.5} style={{ minWidth: 0 }}>
        <Brand
          size="small"
          variant="compact"
          brandName={selectedBrandLabel ?? ""}
          logoSrcCompact={
            selectedMatch.brand.brandLogoUrl?.trim() || undefined
          }
        />
        <Flex direction="column" style={{ minWidth: 0, textAlign: "left" }}>
          <Paragraph className="m-0 text-grey-700 font-bold text-truncate">
            {selectedBrandLabel}
          </Paragraph>
          <Paragraph className="m-0 text-grey-500 text-truncate">
            {selectedOrgLabel}
          </Paragraph>
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex
      direction="column"
      gap={0.25}
      className="config-org-brand-selector"
    >
      <Menu
        items={menuItems}
        onSelect={handleMenuSelect}
        aria-label="Select organization and brand"
        placement="bottom start"
        style={{ height: "200px !important", overflowY: "auto" }}
      >
        <Button
          variant="clear"
          size="small"
          color="grey"
          className="config-org-brand-selector__trigger border-1 border-color-grey-25 bg-white"
          isDisabled={organizationsBrandsLookupLoading}
          style={{ justifyContent: "space-between", minWidth: 280 }}
        >
          <Flex align="center" gap={0.5} style={{ minWidth: 0, flex: 1 }}>
            {menuContent}
          </Flex>
          <Icon name={Icons.ChevronDown} />
        </Button>
      </Menu>
    </Flex>
  );
};
